/**
 * The retro kit's one list (slice 6b): rows in a `center` panel with the
 * cursor (`ui.menu.cursor`, ▶) before the selected one and a right-aligned
 * value column (prices, settings). Every menu of the game is this component;
 * there is no second list style.
 *
 * Its title (Q52, ADR 0034): not in the window's body but in a small `tab`
 * panel over the window's top-left corner, across its edge (Bravely Default's
 * way), and only when the menu has one; a menu whose title would repeat its
 * context (the course at the wheel, the eggs at an incubator) has none, and
 * its `label` names it for a screen reader. The Cancel or Leave row stays: a
 * tap interface needs it (the designer, Q52).
 *
 * Input: keys while it is open (arrows move, Enter, Space or E confirms,
 * Escape or Backspace closes; listened for in the capture phase and marked
 * handled, so the shell under it ignores them), the pad through `nav` (the
 * shell maps its buttons to up, down, confirm and close), and taps: a row is
 * a 44 px target, a tap selects it and a tap on the selected row confirms.
 * A disabled row is dimmed, not hidden, and the cursor passes over it. A full
 * screen catcher under the panel takes every other tap, so nothing reaches
 * the canvas while a menu is open.
 *
 * Adjustable rows (slice 8f, the packing panel's counts): with `onAdjust`, a
 * row marked `adjustable` changes its value by one with the left and right
 * arrows, the pad's left and right (`nav`'s `left`, `right`), or a tap on the
 * `−` or `+` either side of its value (`ui.menu.less`, `ui.menu.more`; each a
 * 44 px button beside the row's own, never inside it, that selects the row and
 * never confirms it; the value between them is inert). The row tells a screen
 * reader its keys (`aria-keyshortcuts`) and its value (`aria-valuetext`, and
 * `aria-valuenow`/`min`/`max` with a `range`). The menu only asks: the value is
 * its owner's.
 *
 * Headings and lines (slice 8f fix round): a `heading` row is a heading, not
 * a menu item (the cursor passes over it); a row `under` a heading is set in
 * and described by it. `lines` sit above the rows, outside their scroll, so a
 * status ("The bag is full.") is always in view. A `waiting` row is dimmed but
 * may still be confirmed: its owner waits for what it needs first. Since slice
 * 8h, lines `below` the rows follow the cursor (asked for the cursor's row each
 * time it moves: the skip's confirmation says what that row's tickets waste),
 * and a line may be a `warning` (the kit's warning colour, `--ab-warning`).
 *
 * The cursor (slice 8f): it starts on `initial` when that row is enabled, else
 * on the first enabled row; it follows its row by key when the rows change
 * under it (a row added above or taken away), and when its row is gone it
 * stays at the same place and takes the key of the row now there (`cursorAt`).
 * The rows scroll inside the window, and the cursor's row is kept in view as
 * it moves.
 *
 * Focus (slice 6b fix round): the list (`role="menu"`) is the menu's one
 * focusable element; it takes the focus when the menu opens and names the
 * cursor's row (`aria-activedescendant`; the row has `aria-current`); the rows
 * are not tabbable, Tab stays in the menu, and closing gives the focus back to
 * what had it. Enter always confirms the cursor's row, which is the row a
 * screen reader is told is current.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { Strings } from '@ab/session';
import { useModalFocus } from './focus.ts';
import { type MenuNav, type NavCommand, cursorAt, firstEnabled, navForKey, selectable, startRow, stepForKey } from './nav.ts';
import { Panel } from './panel.tsx';
import { text } from './text.ts';
import './ui.css';

export type MenuRow = {
  readonly key: string;
  readonly label: string;
  readonly value?: string;
  readonly disabled?: boolean;
  /** Its value changes by one with left and right (the menu's `onAdjust`). */
  readonly adjustable?: boolean;
  /** An adjustable value's number and bounds, for a screen reader. */
  readonly range?: { readonly now: number; readonly min: number; readonly max: number };
  /** A heading over the rows `under` it: not a menu item, the cursor passes over it. */
  readonly heading?: boolean;
  /** The key of the heading this row sits under: set in, and described by it. */
  readonly under?: string;
  /** Dimmed while its owner waits for something; it may still be confirmed. */
  readonly waiting?: boolean;
};

/** A line a menu says above (or below) its rows, outside their scroll; `warning` in the warning colour (slice 8h). */
export type MenuLine = { readonly key: string; readonly text: string; readonly tone?: 'warning' };

export type MenuProps = {
  readonly strings: Strings;
  /** Shown in the tab over the window's corner (a shop's keeper, the party); none for the pause menu. Also the menu's accessible name. */
  readonly title?: string;
  /** The accessible name when there is no title. */
  readonly label?: string;
  readonly rows: readonly MenuRow[];
  readonly onConfirm: (key: string) => void;
  readonly onClose: () => void;
  /** An adjustable row's value one step down (-1) or up (1). */
  readonly onAdjust?: (key: string, step: -1 | 1) => void;
  /** The row the cursor starts on (when it is enabled). */
  readonly initial?: string;
  /** Lines above the rows, outside their scroll (a status the menu says). */
  readonly lines?: readonly MenuLine[];
  /**
   * Lines below the rows, outside their scroll, for the row under the cursor (slice 8h: the skip's confirmation says
   * the time left and what the cursor's choice wastes): asked again whenever the cursor moves.
   */
  readonly below?: (cursor: string | null) => readonly MenuLine[];
  readonly nav?: MenuNav;
  readonly testId?: string;
};

/** A menu's lines, above or below its rows (announced as they change). */
const Lines = ({ lines, place }: { readonly lines: readonly MenuLine[]; readonly place: 'above' | 'below' }) =>
  lines.length === 0 ? null : (
    <div className="ab-menu-lines" data-place={place} aria-live="polite">
      {lines.map((line) => (
        <p key={line.key} className="ab-menu-line" data-key={line.key} {...(line.tone === undefined ? {} : { 'data-tone': line.tone })}>
          {line.text}
        </p>
      ))}
    </div>
  );

export const Menu = ({ strings, title, label, rows, onConfirm, onClose, onAdjust, initial, lines = [], below, nav, testId = 'menu' }: MenuProps) => {
  // The cursor is its row's key, so it follows the row when rows come and go above it.
  const [cursorKey, setCursorKey] = useState<string | null>(() => startRow(rows, initial));
  const lastIndex = useRef(0);
  const at = cursorAt(rows, cursorKey, lastIndex.current);
  const cursor = at.index;
  lastIndex.current = cursor;
  // Its row gone, the cursor stays at its place and takes that row's key (a row coming back later never pulls it).
  useEffect(() => {
    if (at.key !== cursorKey) setCursorKey(at.key);
  }, [at.key, cursorKey]);
  const setCursor = (i: number): void => {
    setCursorKey(rows[i]?.key ?? null);
  };
  const current = useRef({ cursor, rows, onConfirm, onClose, onAdjust, setCursor });
  current.current = { cursor, rows, onConfirm, onClose, onAdjust, setCursor };
  const list = useRef<HTMLUListElement>(null);
  useModalFocus(list);
  const id = useId();
  useEffect(() => {
    const act = (command: NavCommand): void => {
      const { cursor: c, rows: r, onConfirm: confirm, onClose: close, onAdjust: adjust, setCursor: move } = current.current;
      switch (command) {
        case 'up':
          move(firstEnabled(r, c - 1, -1));
          return;
        case 'down':
          move(firstEnabled(r, c + 1, 1));
          return;
        case 'confirm': {
          const row = r[c];
          if (row !== undefined && selectable(row)) confirm(row.key);
          return;
        }
        case 'close':
          close();
          return;
        case 'left':
        case 'right': {
          const row = r[c];
          if (adjust !== undefined && row?.adjustable === true && selectable(row)) adjust(row.key, command === 'left' ? -1 : 1);
          return;
        }
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Tab') {
        // The menu keeps the focus: nothing behind it is reachable while it is open.
        e.preventDefault();
        list.current?.focus({ preventScroll: true });
        return;
      }
      // Left and right only where they change something (a menu with adjustable rows), so other menus leave them be.
      const command = navForKey(e.key) ?? (current.current.onAdjust === undefined ? null : stepForKey(e.key));
      if (command === null) return;
      e.preventDefault();
      if (e.repeat && command !== 'up' && command !== 'down' && command !== 'left' && command !== 'right') return;
      act(command);
    };
    window.addEventListener('keydown', onKey, true);
    const off = nav?.subscribe(act);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      off?.();
    };
  }, [nav]);
  // The cursor's row stays in view in a list longer than the window.
  useEffect(() => {
    document.getElementById(`${id}-${cursor}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [id, cursor]);
  return (
    <>
      <div
        className="ab-veil"
        data-testid={`${testId}-veil`}
        onPointerDown={(e) => {
          e.preventDefault();
        }}
      />
      <Panel placement="center" className="ab-menu" data-testid={testId} data-titled={title !== undefined}>
        {title !== undefined && (
          <Panel placement="tab" className="ab-menu-title" data-testid={`${testId}-title`} aria-hidden="true">
            {title}
          </Panel>
        )}
        <Lines lines={lines} place="above" />
        <ul ref={list} role="menu" tabIndex={0} aria-label={title ?? label} aria-activedescendant={`${id}-${cursor}`}>
          {rows.map((row, i) => {
            if (row.heading === true) {
              return (
                <li key={row.key} role="none" className="ab-menu-heading" data-key={row.key} id={`${id}-h-${row.key}`}>
                  <span role="heading" aria-level={2}>
                    {row.label}
                  </span>
                </li>
              );
            }
            const adjustable = onAdjust !== undefined && row.adjustable === true && row.disabled !== true;
            const step = (by: -1 | 1) => () => {
              // A tap on − or + selects the row and changes its value; it never confirms the row.
              list.current?.focus({ preventScroll: true });
              setCursor(i);
              onAdjust?.(row.key, by);
            };
            const stepButton = (by: -1 | 1) => {
              const mark = text(strings, by === -1 ? 'ui.menu.less' : 'ui.menu.more');
              return (
                <button type="button" tabIndex={-1} className="ab-menu-step" data-step={by === -1 ? 'less' : 'more'} aria-label={mark} onClick={step(by)}>
                  {mark}
                </button>
              );
            };
            return (
              <li key={row.key} role="none" className={adjustable ? 'ab-menu-adjustable' : undefined}>
                <button
                  type="button"
                  role="menuitem"
                  id={`${id}-${i}`}
                  tabIndex={-1}
                  className="ab-menu-row"
                  data-key={row.key}
                  data-selected={i === cursor}
                  data-under={row.under !== undefined}
                  data-waiting={row.waiting === true}
                  aria-current={i === cursor ? 'true' : undefined}
                  aria-disabled={row.disabled === true}
                  aria-busy={row.waiting === true ? true : undefined}
                  aria-describedby={row.under === undefined ? undefined : `${id}-h-${row.under}`}
                  {...(adjustable
                    ? {
                        'aria-keyshortcuts': 'ArrowLeft ArrowRight',
                        'aria-valuetext': row.value,
                        ...(row.range === undefined ? {} : { 'aria-valuenow': row.range.now, 'aria-valuemin': row.range.min, 'aria-valuemax': row.range.max }),
                      }
                    : {})}
                  onClick={() => {
                    list.current?.focus({ preventScroll: true });
                    if (row.disabled === true) return;
                    if (i === cursor) onConfirm(row.key);
                    else setCursor(i);
                  }}
                >
                  <span className="ab-menu-cursor" aria-hidden="true">
                    {i === cursor ? text(strings, 'ui.menu.cursor') : ''}
                  </span>
                  <span className="ab-menu-label">{row.label}</span>
                  {row.value !== undefined && !adjustable && <span className="ab-menu-value">{row.value}</span>}
                </button>
                {adjustable && (
                  <span className="ab-menu-value ab-menu-adjust">
                    {stepButton(-1)}
                    <span className="ab-menu-amount" data-key={`${row.key}:amount`} aria-hidden="true">
                      {row.value}
                    </span>
                    {stepButton(1)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        {below !== undefined && <Lines lines={below(rows[cursor]?.key ?? null)} place="below" />}
      </Panel>
    </>
  );
};
